if (!window.customElements.get('custom-lookbook')) {
  window.customElements.define('custom-lookbook',
	class Lookbook extends HTMLElement {
	  constructor() {
		super();
		// DOM container elements
		this.section = null;       // The main section container (.section-lookbook)
		this.wrapper = null;       // The lookbook wrapper container (.lookbook__wrapper)
		this.slidesContainer = null; // Container for all slides (.lookbook__slides)
		
		// Content elements
		this.slides = null;        // All individual slides (.lookbook__slide)
		
		// UI controls
		this.paginationDesktop = null; // Desktop pagination controls (.lookbook__pagination--desktop)
		this.paginationMobile = null;  // Mobile pagination controls (.lookbook__pagination--mobile)
		
		// State tracking
		this.resizeObserver = null;    // Observer that tracks section size changes
		this.currentSlideIndex = 0;    // Tracks the current active slide
		this.swiperInstance = null;    // Reference to the mobile Swiper instance
	  }

	  connectedCallback() {
		// Initialize DOM references
		this.initDomReferences();
		
		// Initialize when element is connected to DOM
		this.init();
		
		// Setup event listeners for Shopify section events
		document.addEventListener("shopify:section:load", this.handleSectionLoad.bind(this));
		document.addEventListener("shopify:section:reorder", this.handleSectionReorder.bind(this));
	  }
	  
	  initDomReferences() {
		// Find and store references to all required DOM elements
		this.section = this.closest(".section-lookbook");
		this.wrapper = this.querySelector(".lookbook__wrapper");
		this.slidesContainer = this.querySelector(".lookbook__slides");
		this.slides = this.querySelectorAll(".lookbook__slide");
		this.paginationDesktop = this.querySelector(".lookbook__pagination--desktop");
		this.paginationMobile = this.querySelector(".lookbook__pagination--mobile");
	  }

	  disconnectedCallback() {
		// Clean up resources when element is removed from DOM
		this.resizeObserver?.disconnect();
		
		// Remove event listeners to prevent memory leaks
		document.removeEventListener("shopify:section:load", this.handleSectionLoad.bind(this));
		document.removeEventListener("shopify:section:reorder", this.handleSectionReorder.bind(this));
		
		// Properly destroy the Swiper instance if it exists
		this.swiperInstance?.destroy(true, true);

		// Clean up any ScrollTrigger instances
		ScrollTrigger.getAll().forEach(trigger => {
		  if (trigger.vars.id === "horizontalScroll") {
			trigger.kill();
		  }
		});
	  }

	  handleSectionLoad() {
		console.log("shopify:section:load");
		this.resize();
		this.initDesktop();
	  }

	  handleSectionReorder() {
		console.log("shopify:section:reorder");
		this.resize();
		this.initDesktop();
	  }

	  init() {
		this.resize();
		this.initDesktop();
	  }

	  initDesktop() {
		// Register ScrollTrigger
		gsap.registerPlugin(ScrollTrigger);

		// Generate pagination bullets
		this.generatePagination(this.slides, this.paginationDesktop);

		// Handle hover events
		this.paginationBulletsHoverEvents(this.paginationDesktop);

		ScrollTrigger.matchMedia({
		  "(min-width: 991px)": () => {
			// Convert NodeList to array for GSAP animations
			const slides = gsap.utils.toArray(this.slides);
			let maxWidth = 0;

			slides.forEach((slide) => {
			  maxWidth += slide.offsetWidth;
			});

			// Pin desktop pagination if needed
			if (this.paginationDesktop) {
			  gsap.to(this.wrapper, {
				scrollTrigger: {
				  trigger: this.wrapper,
				  pin: this.paginationDesktop,
				  markers: false,
				  start: "top 128px",
				  end: () => `+=${maxWidth + 128}`,
				  pinSpacing: true
				}
			  });
			}

			// Pin slides
			let tween = gsap.to(slides, {
			  x: () => `-${maxWidth - window.innerWidth / 1.03}`,
			  ease: "linear",
			  scrollTrigger: {
				id: "horizontalScroll", // Set an ID to reference later
				trigger: this.wrapper,
				pin: this.wrapper, // this.slidesContainer,
				scrub: 1,
				markers: false,
				pinSpacing: true,
				anticipatePin: 1,
				start: () => "top 128px",
				end: () => `+=${maxWidth}`,
				onUpdate: (self) => {
				  let activeSlideIndex = -1;
				  const triggerPoint = window.innerWidth / 4;
				  
				  slides.forEach((slide, index) => {
					const slideRect = slide.getBoundingClientRect();
					
					if (slideRect.left <= triggerPoint && slideRect.right >= triggerPoint) {
					  activeSlideIndex = index;
					}
				  });
		  
				  if (activeSlideIndex !== -1) {
					this.updateActiveBullet(activeSlideIndex, this.paginationDesktop);
				  }
				}
			  }
			});
			
			// Handle pagination bullet clicks
			const bullets = this.paginationDesktop?.querySelectorAll('.lookbook__pagination-bullet') ?? [];
			bullets.forEach((bullet, index) => {
			  bullet.addEventListener('click', () => {
				const scrollTriggerInstance = ScrollTrigger.getById("horizontalScroll");
				if (!scrollTriggerInstance) return;

				// Get the normalized position from the data attribute
				const normalizedPosition = parseFloat(bullet.dataset.normalizedPosition || 0);
				const totalScroll = maxWidth - window.innerWidth / 1.03;
				const targetScroll = -(totalScroll * normalizedPosition);

				// Calculate the scroll position needed for ScrollTrigger
				let scrollPosition = scrollTriggerInstance.start + ((scrollTriggerInstance.end - scrollTriggerInstance.start) * normalizedPosition);
				
				// Check if the calculated position exceeds the pin end boundary
				if (scrollPosition > scrollTriggerInstance.end) {
				  // Clamp to the end position (just before unpinning)
				  scrollPosition = scrollTriggerInstance.end - 1;
				  console.log('Clamping to end of pinned section');
				}

				// First update the scroll position
				window.scrollTo({
				  top: scrollPosition,
				  behavior: 'smooth'
				});

				// Then animate the slides
				gsap.to(slides, {
				  duration: 1,
				  x: targetScroll,
				  markers: false,
				  ease: "power2.out",
				  onUpdate: () => {
					// Keep ScrollTrigger in sync during the animation
					const currentProgress = gsap.getProperty(slides[0], "x") / -(totalScroll);
					let syncScrollPosition = scrollTriggerInstance.start + ((scrollTriggerInstance.end - scrollTriggerInstance.start) * currentProgress);
					
					// Ensure we don't exceed the pin boundaries
					if (syncScrollPosition > scrollTriggerInstance.end) {
					  syncScrollPosition = scrollTriggerInstance.end - 1;
					}
					
					scrollTriggerInstance.scroll(syncScrollPosition);
					scrollTriggerInstance.update();
				  },
				  onComplete: () => {
					this.updateActiveBullet(index, this.paginationDesktop);
					// Final sync of ScrollTrigger, ensuring we don't exceed boundaries
					let finalScrollPosition = scrollPosition;
					if (finalScrollPosition > scrollTriggerInstance.end) {
					  finalScrollPosition = scrollTriggerInstance.end - 1;
					}
					scrollTriggerInstance.scroll(finalScrollPosition);
					scrollTriggerInstance.update();
				  }
				});
			  });
			});
	  
			// Set initial active bullet
			this.updateActiveBullet(0, this.paginationDesktop);
		  },
		});
	  }

	  calculateSlidePositions(slides) {
		// Calculate the scroll position and width data for each slide
		let positions = [];
		let cumulativeWidth = 0;
		let totalWidth = 0;
		
		// First pass: calculate total width
		slides.forEach(slide => {
		  totalWidth += slide.offsetWidth;
		});
		
		// Second pass: calculate normalized positions
		slides.forEach(slide => {
		  const slideWidth = slide.offsetWidth;
		  const position = cumulativeWidth;
		  const normalizedPosition = position / (totalWidth - window.innerWidth / 1.03);
		  
		  positions.push({
			position: position,
			normalizedPosition: normalizedPosition, // 0 to 1 value
			width: slideWidth,
			totalWidth: totalWidth
		  });
		  
		  cumulativeWidth += slideWidth;
		});
		
		return positions;
	  }
	  
	  generatePagination(slides, pagination) {
		// Create pagination bullets for desktop view
		if (!pagination) return;
		
		// Clear existing pagination bullets
		pagination.innerHTML = '';
		
		// Calculate slide positions
		const slidePositions = this.calculateSlidePositions(slides);
		
		// Create a bullet for each slide
		slides.forEach((slide, index) => {
		  const bullet = document.createElement('button');
		  bullet.className = 'lookbook__pagination-bullet';
		  bullet.setAttribute('aria-label', `Go to slide ${index + 1}`);
		  
		  // Add data attributes
		  bullet.dataset.index = index;
		  bullet.dataset.position = slidePositions[index].position;
		  bullet.dataset.normalizedPosition = slidePositions[index].normalizedPosition.toFixed(4);
		  bullet.dataset.width = slidePositions[index].width;
		  bullet.dataset.totalWidth = slidePositions[index].totalWidth;
		  
		  bullet.innerHTML = `<span class="lookbook__pagination-bullet-bar"></span>`;
		  bullet.innerHTML += `<span class="lookbook__pagination-bullet-number">${slide.dataset.paginationLabel}</span>`;
		  pagination.appendChild(bullet);
		});
	  }

	  paginationBulletsHoverEvents(pagination) {
		// Add hover effects to pagination bullets
		if (!pagination) return;
		
		// First remove any existing event listeners to prevent duplicates
		pagination.querySelectorAll('.lookbook__pagination-bullet').forEach(bullet => {
		  bullet.removeEventListener('mouseenter', (e) => pagination.classList.add('is-hover'));
		  bullet.removeEventListener('mouseleave', (e) => pagination.classList.remove('is-hover'));
		});
		
		// Add hover effects only to non-active bullets
		pagination.querySelectorAll('.lookbook__pagination-bullet:not(.active)').forEach(bullet => {
		  bullet.addEventListener('mouseenter', (e) => pagination.classList.add('is-hover'));
		  bullet.addEventListener('mouseleave', (e) => pagination.classList.remove('is-hover'));
		});
	  }

	  updateActiveBullet(index, pagination) {
		// Update the active state of pagination bullets
		if (!pagination) return;
		
		const bullets = pagination.querySelectorAll('.lookbook__pagination-bullet');
		
		// Toggle active class on the correct bullet
		bullets.forEach((bullet, i) => {
		  bullet.classList.toggle('active', i === index);
		});

		// Refresh hover event listeners after changing active state
		this.paginationBulletsHoverEvents(pagination);
	  }

	  updateMobileBullet(index) {
		// Update the active state of mobile pagination bullets
		if (!this.paginationMobile) return;
		
		const bullets = this.paginationMobile.querySelectorAll('.swiper-pagination-bullet');
		
		// Update active state and styling for each bullet
		bullets.forEach((bullet, i) => {
		  // Toggle active class based on current index
		  bullet.classList.toggle('active', i === index);

		  // Apply special styling to active bullet or reset inactive bullets
		  if (i === index) {
			// Custom styling handled by CSS :after pseudo-element
			this.currentSlideIndex = index; // Update current slide tracking
		  } else {
			bullet.style.removeProperty('--bullet-number');
		  }
		});
	  }

	  resize() {
		if (!this.section) return;
		
		// Disconnect previous observer if it exists
		this.resizeObserver?.disconnect();

		this.resizeObserver = new ResizeObserver((entries) => {
		  const [entry] = entries;

		  if (entry.contentRect.width < 991) {
			// for mobile 100vh viewport
			let vh = window.innerHeight * 0.01;
			document.documentElement.style.setProperty("--vh", `${vh}px`);

			this.wrapper
			  .querySelector(".lookbook__slides")
			  .classList.add("swiper-wrapper");
			  
			this.wrapper
			  .querySelectorAll(".lookbook__slide")
			  .forEach((slide) => slide.classList.add("swiper-slide"));
  
			// Initialize mobile swiper
			this.swiperInstance = new Swiper(this.wrapper, {
				loop: false,
				freeMode: {
				  enabled: true,
				  momentum: true,
				},
				initialSlide: 0,
				pagination: {
				  el: '.lookbook__pagination--mobile',
				  type: 'bullets',
				  clickable: true,
				  renderBullet: (index, className) => {
					let bullet = `<span class="lookbook__pagination-bullet-number">${this.slides[index].dataset.paginationLabel}</span>`;
					return `<div class="dot swiper-pagination-bullet">${bullet}</div>`
				  },
				},
				breakpoints: {
				  320: {
					slidesPerView: "auto",
				  },
				  640: {
					slidesPerView: "auto",
				  },
				},
				on: {
				  slideChange: () => {
					this.updateMobileBullet(this.swiperInstance.activeIndex);
				  },
				},
			});
		  } else {
			// for mobile 100vh viewport
			let vh = window.innerHeight * 0.01;
			document.documentElement.style.setProperty("--vh", `${vh}px`);

			this.swiperInstance?.destroy(true, true);
			this.wrapper
				.querySelector(".lookbook__slides")
				.classList.remove("swiper-wrapper");
				
			this.wrapper
				.querySelectorAll(".lookbook__slide")
				.forEach((slide) => slide.classList.remove("swiper-slide"));
		  }
		});

		this.resizeObserver.observe(this.section);
	  }
	}
  );
}